import { Controller } from "@hotwired/stimulus"
import { Collapse } from "bootstrap"

// Backs the docs page's "On this page" sidebar (see help/index.html.erb). Two jobs:
//
// Scroll tracking: highlights the link for whichever section is currently being read and opens
// its group. A group this controller opened is closed again once reading moves on to another
// group; one the reader opened by hand is never touched, so their own choices stay put.
//
// Filtering: the page is one long document with no search, so the box at the top narrows the
// links down to matching topics and opens every group that still has a match.
export default class extends Controller {
  static targets = ["link", "group", "filter", "empty"]

  connect() {
    this.autoOpened = new Set()
    this.sections = this.linkTargets
      .map(link => document.getElementById(link.hash.slice(1)))
      .filter(Boolean)

    // A section counts as "being read" once its top has passed a line just under the sticky
    // navbar; of those, the one closest to that line wins. Compared by position, not by list
    // order, since the sidebar groups topics differently from the order they appear on the page.
    this.onScroll = this.onScroll.bind(this)
    window.addEventListener("scroll", this.onScroll, { passive: true })
    this.onScroll()
  }

  disconnect() {
    window.removeEventListener("scroll", this.onScroll)
  }

  onScroll() {
    if (this.filtering) return
    if (this.frame) return
    this.frame = requestAnimationFrame(() => {
      this.frame = null
      const line = 120
      let current = null
      let currentTop = -Infinity
      for (const section of this.sections) {
        const top = section.getBoundingClientRect().top
        if (top <= line && top > currentTop) {
          current = section
          currentTop = top
        }
      }
      this.activate(current)
    })
  }

  activate(section) {
    const id = section?.id
    if (id === this.activeId) return
    this.activeId = id

    let activeGroup = null
    for (const link of this.linkTargets) {
      const active = link.hash === `#${id}`
      link.classList.toggle("active", active)
      if (active) {
        link.setAttribute("aria-current", "location")
        activeGroup = link.closest("[data-docs-toc-target='group']")
      } else {
        link.removeAttribute("aria-current")
      }
    }

    for (const group of this.groupTargets) {
      const collapse = group.querySelector(".accordion-collapse")
      if (group === activeGroup) {
        if (!collapse.classList.contains("show")) {
          this.autoOpened.add(group)
          Collapse.getOrCreateInstance(collapse, { toggle: false }).show()
        }
      } else if (this.autoOpened.has(group)) {
        this.autoOpened.delete(group)
        Collapse.getOrCreateInstance(collapse, { toggle: false }).hide()
      }
    }
  }

  filter() {
    const query = this.filterTarget.value.trim().toLowerCase()
    this.filtering = query.length > 0
    let anyMatch = false

    for (const group of this.groupTargets) {
      const collapse = group.querySelector(".accordion-collapse")
      const groupLabel = group.querySelector(".accordion-button").textContent.toLowerCase()
      const groupMatches = this.filtering && groupLabel.includes(query)
      let visible = 0

      for (const link of group.querySelectorAll("[data-docs-toc-target='link']")) {
        const match = !this.filtering || groupMatches || link.textContent.toLowerCase().includes(query)
        link.closest("li").hidden = !match
        if (match) visible++
      }

      group.hidden = this.filtering && visible === 0
      if (visible > 0 && this.filtering) {
        anyMatch = true
        collapse.classList.add("show")
      } else if (!this.filtering) {
        collapse.classList.toggle("show", this.autoOpened.has(group) || group.dataset.userOpened === "true")
      }
    }

    this.emptyTarget.hidden = !this.filtering || anyMatch
    if (!this.filtering) this.onScroll()
  }

  // Remembers which groups the reader opened or closed by hand (the header button), so they're
  // restored as they were once a filter is cleared, and never auto-closed by scroll tracking.
  toggled(event) {
    const group = event.target.closest("[data-docs-toc-target='group']")
    const collapse = group.querySelector(".accordion-collapse")
    this.autoOpened.delete(group)
    // The click fires before Bootstrap flips the class, so the new state is the opposite of now.
    group.dataset.userOpened = String(!collapse.classList.contains("show"))
  }
}
